
import { useEffect } from "react";

import { 
    ActionStatusHelper, 
    DeleteAction, 
    FeedBackType, 
    LONG_PROCESS_TOAST_DURATION, 
    MessageHelper, 
    ResponseHook, 
    UIFeedbackHooks 
} from "@/Shared";


export interface UseDeleteDomainOptions<T> {
    /** Domain label used in messages (e.g. "User", "Contact") */
    domainLabel: string;

    /** Whether the current user is allowed to delete */
    canDelete: boolean;

    canRestore?: boolean;

    deleteAction : DeleteAction<T>;

    /** UI feedback hooks (toast, confirm) */
    uiFeedbackHooks: UIFeedbackHooks;

    /** Callback executed after a successful delete */
    onSuccess?: () => void;
}


/**
 * useDeleteDomain hook
 *
 * Generic domain-level hook responsible for handling
 * delete workflows in a consistent and reusable way.
 *
 * It orchestrates:
 * - permission checks
 * - confirmation dialog
 * - async delete execution
 * - toast feedback (loading / success / error)
 *
 * This hook is UI-agnostic and domain-agnostic.
 * It relies only on injected callbacks and responses.
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export function useDeleteDomain<T>(options: UseDeleteDomainOptions<T>) {
    const {
        domainLabel,
        canDelete,
        canRestore,
        deleteAction,
        uiFeedbackHooks,
        onSuccess,
    } = options;

    const { toastHook, confirmHook } = uiFeedbackHooks;
    const {execute, response, setResponse} = deleteAction;

    /**
     * Watches the delete async response and triggers
     * the appropriate UI feedback.
     */
    useEffect(() => {
        const { isSucceeded, isFailed } = ActionStatusHelper.is(
            response.status
        );

        if (isSucceeded) {
            toastHook.openToast?.({
                data: {
                    message: MessageHelper.DELETING.SUCCESS(domainLabel),
                    type: FeedBackType.SUCCESS,
                },
                onClose: () => {
                    ResponseHook.reset(setResponse);
                    onSuccess?.();
                }
            });
        }

        if (isFailed) {
            toastHook.openToast?.({
                data: {
                    message:
                        response.error ??
                        MessageHelper.DELETING.ERROR(domainLabel),
                    type: FeedBackType.ERROR,
                },
                onClose: () => ResponseHook.reset(setResponse)
            });
        }
    }, [response.status, response.error]);

    /**
     * Executes the delete workflow:
     * 1. Permission check
     * 2. User confirmation
     * 3. Loading feedback
     * 4. Delete API call
     */
    const executeDelete = async (entityId : number | string) => {
        if (!canDelete) {
            toastHook.openToast?.({
                data: {
                    message: MessageHelper.DELETING.NOT_ALLOWED(domainLabel),
                    type: FeedBackType.WARNING,
                }
            });
            return;
        }

        const confirmed = await confirmHook.openConfirm?.({
            title: MessageHelper.MODAL.CONFIRM.TITLE,
            content: MessageHelper.MODAL.CONFIRM.DELETING(domainLabel),
        });

        if (!confirmed || !execute || !entityId) {
            return;
        }

        toastHook.openToast?.({
            data: {
                message: MessageHelper.DELETING.LOADING(domainLabel),
                type: FeedBackType.INFO,
                duration: LONG_PROCESS_TOAST_DURATION
            }
        });

        await execute(entityId, canRestore ?? false);
    };

    /**
     * Public API
     */
    return {
        executeDelete,
    };
}